import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Input, Spinner, Chip, Tooltip, Avatar } from '@heroui/react';
import { useNavigate } from 'react-router';
import { Icon } from '@iconify/react';
import { useTranslations } from '../../../../../contexts/translation.context';
import { Typography, UniversalTable, IdView } from '../../../../../components';
import { FilterSelect } from '../../../../../components/ui';
import {
  SearchInputIc,
  ResetFiltersIc,
  GroupUserIc,
  ChatRoundedIc,
  MoneyIc,
  RefreshGreyIc,
  EmptyContentIc,
  OnlyFansIc,
  InstagramIc,
  TwitterIc,
  GoogleIc,
  IcReddit,
  IcTikTok,
  IcTg,
  IcYoutube,
  IcAnotherSoc,
  GlobalIc,
} from '../../../../../components/icons';
import {
  refShareApi,
  RefShareOrder,
  OrderStatus,
  OwnerLevel,
  TrafficSource,
} from '../../../../../api/services';
import { formatTimeAgo } from '../../../../../utils/formatTimeAgo';
import { showError } from '../../../../../utils/toast';
import { parseApiError } from '../../../../../utils/errorParser';
import CampaignActionsCell from '../../../../revshare/campaigns/components/CampaignActionsCell';

interface PartnerRevShareCampaignsTabProps {
  partnerId: string;
}

const PLATFORM_COMMISSION = 5;

const levelConfig: Record<OwnerLevel, { bgColor: string; textColor: string; label: string }> = {
  new: { bgColor: 'rgba(23, 201, 100, 0.15)', textColor: '#17C964', label: 'New' },
  base: { bgColor: '#EEEEEF', textColor: '#71717A', label: 'Base' },
  pro: { bgColor: '#11181c', textColor: '#ffffff', label: 'Pro' },
  expert: { bgColor: 'rgba(120, 40, 200, 0.15)', textColor: '#7828c8', label: 'Expert' },
  bronze: { bgColor: '#FDF4E7', textColor: '#CD7F32', label: 'Bronze' },
  silver: { bgColor: '#F0F0F0', textColor: '#808080', label: 'Silver' },
  gold: { bgColor: '#FFF9E6', textColor: '#FFD700', label: 'Gold' },
  platinum: { bgColor: '#E8F4F8', textColor: '#A0B2C6', label: 'Platinum' },
  diamond: { bgColor: '#E6F3FF', textColor: '#00BFFF', label: 'Diamond' },
};

const getStatusConfig = (status: OrderStatus) => {
  switch (status) {
    case 'in_progress':
      return { text: 'Активная', dotColor: 'bg-[#17C964]' };
    case 'waiting':
      return { text: 'Принята', dotColor: 'bg-[#F5A524]' };
    case 'in_moderation':
      return { text: 'На модерации', dotColor: 'bg-[#7828C8]' };
    case 'canceled':
      return { text: 'Отменена', dotColor: 'bg-[#F31260]' };
    case 'completed':
    default:
      return { text: 'Неактивная', dotColor: 'bg-[#71717A]' };
  }
};

const getSourceIcon = (source: string, size: number = 14) => {
  const iconMap: Record<string, React.ReactElement> = {
    only_fans: <OnlyFansIc size={size} fill="#A8A8B0" />,
    reddit: <IcReddit size={size} />,
    instagram: <InstagramIc size={size} fill="#A8A8B0" />,
    tiktok: <IcTikTok size={size} />,
    google: <GoogleIc size={size} fill="#A8A8B0" />,
    telegram: <IcTg size={size} />,
    twitter: <TwitterIc size={size} fill="#A8A8B0" />,
    youtube: <IcYoutube size={size} />,
    another: <IcAnotherSoc size={size} />,
  };
  return iconMap[source?.toLowerCase()] || <GlobalIc size={size} />;
};

const getSourceLabel = (source: string) => {
  const labelMap: Record<string, string> = {
    only_fans: 'OnlyFans',
    reddit: 'Reddit',
    instagram: 'Instagram',
    tiktok: 'TikTok',
    google: 'Google',
    telegram: 'Telegram',
    twitter: 'Twitter',
    youtube: 'YouTube',
    another: 'Other',
  };
  return labelMap[source?.toLowerCase()] || source;
};

const PartnerRevShareCampaignsTab: React.FC<PartnerRevShareCampaignsTabProps> = ({ partnerId }) => {
  const { t } = useTranslations();
  const navigate = useNavigate();

  // State
  const [orders, setOrders] = useState<RefShareOrder[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [localSearch, setLocalSearch] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sourceFilter, setSourceFilter] = useState<string>('all');

  // Status options
  const statusOptions = useMemo(() => [
    { key: 'all', label: 'Все статусы' },
    { key: 'in_progress', label: 'Активная' },
    { key: 'in_moderation', label: 'На модерации' },
    // { key: 'waiting', label: 'Принята' },
    { key: 'completed', label: 'Неактивная' },
    { key: 'canceled', label: 'Отменена' },
  ], []);

  // Source options
  const sourceOptions = useMemo(() => [
    { key: 'all', label: 'Все источники' },
    { key: 'instagram', label: 'Instagram' },
    { key: 'tiktok', label: 'TikTok' },
    { key: 'twitter', label: 'Twitter' },
    { key: 'reddit', label: 'Reddit' },
    { key: 'telegram', label: 'Telegram' },
    { key: 'youtube', label: 'YouTube' },
    { key: 'google', label: 'Google' },
    { key: 'another', label: 'Другое' },
  ], []);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return searchValue !== '' || statusFilter !== 'all' || sourceFilter !== 'all';
  }, [searchValue, statusFilter, sourceFilter]);

  // Define table columns
  const columns = useMemo(() => [
    { key: 'id', name: 'ID' },
    { key: 'campaign', name: 'Кампания' },
    { key: 'owner', name: 'Владелец' },
    { key: 'statistics', name: 'Статистика' },
    { key: 'platformIncome', name: 'Доход' },
    { key: 'status', name: 'Статус' },
    { key: 'actions', name: 'Действия' },
  ], []);

  // Fetch orders for partner
  const fetchOrders = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: Record<string, unknown> = {
        page: currentPage,
        page_size: pageSize,
        owner: partnerId,
      };

      if (searchValue.trim()) {
        params.search = searchValue.trim();
      }

      if (statusFilter !== 'all') {
        params.status = statusFilter as OrderStatus;
      }

      if (sourceFilter !== 'all') {
        params.source = sourceFilter as TrafficSource;
      }

      const response = await refShareApi.getOrders(params);
      setOrders(response.objects || []);
      setTotalCount(response.count || 0);
    } catch (error) {
      console.error('Failed to load partner RevShare campaigns:', error);
      setOrders([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [partnerId, currentPage, pageSize, searchValue, statusFilter, sourceFilter]);

  // Load data on mount and when dependencies change
  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Handle status change from table actions
  const handleStatusChange = useCallback(async (orderId: string, newStatus: OrderStatus) => {
    try {
      await refShareApi.changeOrderStatus(orderId, newStatus);
      fetchOrders();
    } catch (error) {
      showError(parseApiError(error, 'Не удалось изменить статус кампании'));
    }
  }, [fetchOrders]);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchValue(localSearch);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [localSearch]);

  // Handlers
  const handleSearchChange = (value: string) => {
    setLocalSearch(value);
  };

  const handleResetFilters = () => {
    setLocalSearch('');
    setSearchValue('');
    setStatusFilter('all');
    setSourceFilter('all');
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  // Format date
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // Cell renderer
  const renderCell = useCallback((order: RefShareOrder, columnKey: string) => {
    const orderId = order._id || order.id || '';

    switch (columnKey) {
      case 'id':
        return (
          <IdView textToCopy={orderId}>
            {orderId.slice(-4)}
          </IdView>
        );

      case 'campaign': {
        const model = order.model || {};
        const modelAvatar = model.avatar;
        const fullTrackLinkId = model.track_link_id ? String(model.track_link_id) : '';
        const shortId = fullTrackLinkId.length > 4 ? fullTrackLinkId.slice(0, 4) : fullTrackLinkId;
        const modelCode = shortId ? `ON${shortId}` : '';
        const modelUsername = model.of_user_name || '';
        const source = order.source || '';
        const campaignType = order.type || 'free';
        const subscriptionType = campaignType === 'free' ? 'FREE' : campaignType === 'free trial' ? 'Free-Trial' : 'FREE';

        return (
          <div className="flex items-start gap-[12px] py-2">
            <Avatar
              src={modelAvatar}
              alt={model.name || modelUsername}
              className="w-8 h-8 shrink-0 rounded-[8px]"
              radius="sm"
            />
            <div className="flex flex-col gap-[4px]">
              <div className="flex items-center gap-[8px] flex-wrap">
                <span className="text-[14px] font-medium text-[#11181c]">{modelCode}</span>
                {modelUsername && (
                  <a
                    href={`https://onlyfans.com/${modelUsername}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[14px] text-[#006fee] hover:underline"
                  >
                    @{modelUsername}
                  </a>
                )}
                <Chip className="bg-[rgba(0,111,238,0.2)] text-[#006fee] rounded-full px-[4px] h-[20px]">
                  <span className="text-[11px]">{subscriptionType}</span>
                </Chip>
              </div>
              {source && (
                <div className="flex items-center gap-[8px]">
                  <Chip
                    className="bg-transparent border-[#d4d4d8] border-[1.4px] rounded-[14px] px-[4px] h-[20px] gap-[2px]"
                    startContent={
                      <div className="w-[12px] h-[12px] flex items-center justify-center">
                        {getSourceIcon(source, 12)}
                      </div>
                    }
                  >
                    <span className="text-[11px] text-[#11181c]">{getSourceLabel(source)}</span>
                  </Chip>
                  <span className="text-[11px] text-[#71717A]">{formatDate(order.created_at)}</span>
                </div>
              )}
            </div>
          </div>
        );
      }

      case 'owner': {
        // Owner (creator/agency) comes from offer.client
        const client = order.offer?.client;
        if (!client) {
          return <span className="text-[#71717A]">—</span>;
        }

        const clientId = client._id || client.id || '';
        const clientType = client.type || 'creator';
        const href = clientType === 'agency' ? `/users/agencies/${clientId}` : `/users/creators/${clientId}`;
        const level = client.lvl;
        const levelStyle = level ? levelConfig[level] : null;

        return (
          <div className="flex items-center gap-[8px] py-2">
            <Avatar
              src={client.avatar}
              name={client.name?.charAt(0)}
              size="sm"
              className="w-[32px] h-[32px] rounded-[8px]"
            />
            <div className="flex flex-col gap-[2px]">
              <div className="flex items-center gap-[6px]">
                <span
                  onClick={() => navigate(href)}
                  className="text-[#006FEE] text-[13px] font-semibold hover:underline cursor-pointer"
                >
                  {client.name}
                </span>
                {levelStyle && (
                  <span
                    className="px-[6px] py-[1px] rounded-full text-[10px] font-medium"
                    style={{
                      backgroundColor: levelStyle.bgColor,
                      color: levelStyle.textColor,
                    }}
                  >
                    {levelStyle.label}
                  </span>
                )}
              </div>
              <Typography variant="body3" className="text-[#71717A] text-[11px]">
                {clientType === 'agency' ? 'Агентство' : 'Криейтор'}
              </Typography>
            </div>
          </div>
        );
      }

      case 'statistics': {
        const subscribers = order.subscribed_count || 0;
        const chats = order.subs?.with_gte_3_chat_messages_count || 0;
        const transactionsTotal = Number(order.transactions_statistic?.total || 0);

        return (
          <div className="flex items-center gap-[12px] py-2">
            <Tooltip content="Подписчики" placement="top">
              <div className="flex items-center gap-[4px] cursor-pointer">
                <GroupUserIc className="w-[14px] h-[14px]" />
                <span className="text-[12px] text-[#11181c]">{subscribers}</span>
              </div>
            </Tooltip>
            <Tooltip content="Сообщения" placement="top">
              <div className="flex items-center gap-[4px] cursor-pointer">
                <ChatRoundedIc className="w-[14px] h-[14px]" />
                <span className="text-[12px] text-[#11181c]">{chats}</span>
              </div>
            </Tooltip>
            <Tooltip content="Всего поступлений" placement="top">
              <div className="flex items-center gap-[4px] cursor-pointer">
                <MoneyIc className="w-[14px] h-[14px]" />
                <span className="text-[12px] text-[#11181c]">${transactionsTotal.toFixed(2)}</span>
              </div>
            </Tooltip>
          </div>
        );
      }

      case 'platformIncome': {
        const totalRevenue = Number(order.transactions_statistic?.total || 0);
        const platformIncome = totalRevenue * (PLATFORM_COMMISSION / 100);

        return (
          <div className="flex items-center gap-2 py-2">
            <span className="text-[14px] font-semibold text-[#11181C]">
              ${platformIncome.toFixed(2)}
            </span>
            <span className="text-[12px] text-[#71717A]">
              ({PLATFORM_COMMISSION}%)
            </span>
          </div>
        );
      }

      case 'status': {
        const statusConfig = getStatusConfig(order.status);
        const lastUpdate = order.last_update || order.created_at;
        const timeAgo = lastUpdate ? formatTimeAgo(lastUpdate, t) : '';

        return (
          <div className="flex flex-col gap-[4px] py-2">
            <Chip
              className="rounded-lg bg-[#F4F4F5] text-[#11181C] h-[24px] px-[8px] py-[4px] gap-[4px]"
              startContent={
                <div className={`w-[7px] h-[7px] rounded-full ${statusConfig.dotColor}`}></div>
              }
            >
              <Typography variant="body3" className="text-[#11181C]">
                {statusConfig.text}
              </Typography>
            </Chip>
            <div className="flex items-center gap-[4px]">
              <Icon icon="solar:calendar-linear" className="w-[12px] h-[12px] text-[#71717A]" />
              <span className="text-[11px] text-[#11181C]">{formatDate(order.created_at)}</span>
            </div>
            {timeAgo && (
              <div className="flex items-center gap-[4px]">
                <RefreshGreyIc size={12} />
                <span className="text-[11px] text-[#71717A]">{timeAgo}</span>
              </div>
            )}
          </div>
        );
      }

      case 'actions':
        return <CampaignActionsCell order={order} onStatusChange={handleStatusChange} />;

      default:
        return null;
    }
  }, [t, navigate, handleStatusChange]);

  // Empty content component
  const emptyContent = (
    <div className="text-center py-[48px]">
      <div className="flex flex-col items-center gap-[16px]">
        <div className="flex items-center flex-col gap-[6px]">
          <EmptyContentIc />
          <Typography variant="h6" className="text-[20px] font-semibold leading-[28px]">
            {hasActiveFilters
              ? 'По выбранным фильтрам нет кампаний'
              : 'Нет кампаний'
            }
          </Typography>
          <Typography className="text-[#A1A1AA] text-[16px] font-medium leading-[24px]" variant="body1">
            {hasActiveFilters
              ? 'Попробуйте изменить параметры фильтров'
              : 'У этого партнера пока нет RevShare кампаний'
            }
          </Typography>
        </div>
      </div>
    </div>
  );

  // Loading state
  if (isLoading && orders.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      {/* Filters Row */}
      <div className="flex items-center gap-[16px] flex-wrap mb-[16px]">
        {/* Search Input */}
        <Input
          type="text"
          placeholder="Поиск по ID или имени"
          size="sm"
          value={localSearch}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSearchChange(e.target.value)}
          endContent={<SearchInputIc />}
          classNames={{
            mainWrapper: "w-[273px]",
            base: "w-[273px]",
            input: "text-sm",
            inputWrapper: "rounded-lg h-[36px] w-[273px]",
          }}
        />

        {/* Status Filter */}
        <FilterSelect
          selectedKey={statusFilter}
          onSelectionChange={(value) => {
            setStatusFilter(String(value));
            setCurrentPage(1);
          }}
          options={statusOptions}
          placeholder="Статус"
          variant="campaign"
          autoWidth={true}
        />

        {/* Source Filter */}
        <FilterSelect
          selectedKey={sourceFilter}
          onSelectionChange={(value) => {
            setSourceFilter(String(value));
            setCurrentPage(1);
          }}
          options={sourceOptions}
          placeholder="Источник"
          variant="campaign"
          autoWidth={true}
        />

        {/* Reset Filters Button */}
        {hasActiveFilters && (
          <Tooltip content="Сбросить фильтры" placement="top">
            <button
              className="h-[36px] w-[36px] rounded-[8px] bg-[#F4F4F5] flex items-center justify-center hover:bg-[#E4E4E7] transition-colors"
              onClick={handleResetFilters}
            >
              <ResetFiltersIc />
            </button>
          </Tooltip>
        )}
      </div>

      {/* Table */}
      <div className="flex-1">
        <UniversalTable
          data={orders}
          columns={columns}
          renderCell={renderCell}
          emptyContent={emptyContent}
          pagination={{
            count: totalCount,
            page_size: pageSize,
            page: currentPage,
          }}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          currentPage={currentPage}
          currentPageSize={pageSize}
        />
      </div>
    </div>
  );
};

export default PartnerRevShareCampaignsTab;
